/* eslint-disable max-lines, no-unused-vars */
import React, {
    useEffect, useMemo, useState
} from 'react';
import {
    useParams
} from 'react-router-dom';
import {
    Play, Loader2, RotateCcw, AlertCircle, CheckCircle2, ChevronRight, Bot, Compass
} from 'lucide-react';
import * as a2aClient from 'shared/api/services/BEX/a2aClient';
import useResourcePickerData from './useResourcePickerData';
import {
    UnsavedBlocker
} from './OpcodeTestPanel';
import {
    Textarea
} from 'shared/components/ui/textarea';
import {
    Input
} from 'shared/components/ui/input';
import {
    Button
} from 'shared/components/ui/button';
import {
    Label
} from 'shared/components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    cn
} from 'shared/lib/utils';
import TypedField, {
    coerceTypedFieldValue
} from './TypedField';

/**
 * AssistantTestPanel — form-driven assistant tester.
 *
 * Form-only (no chat). Every typed_input category is a field set:
 *   - Context: per assistant.typed_input.context_keys
 *   - Propensity: per assistant.typed_input.propensity_keys
 *   - Intent: a single multi-line textarea (this is what users send)
 *   - Entity: per assistant.typed_input.entity_schema.properties (type-aware)
 * Plus an Advanced section for opcode override / channel / memory log id.
 *
 * Schema-driven (UF-13). Live: discovers the union of declared keys + keys
 * referenced via assistant.opcodes[].input_mapping. When the parent updates
 * the assistant prop (typed_input edits flow down via React state), the
 * needed-keys set is recomputed and new fields appear without remount.
 *
 * Layout:
 *   - Default (narrow side panel): inputs section, then result, vertically.
 *   - Maximized: inputs left (~420px), result right (flex-1). 2-pane.
 */
export default function AssistantTestPanel({
    assistant, maximized = false, isDirty = false, onSaveFirst
}) {
    const {
        oragID, subOragID
    } = useParams();

    // ── Field discovery (UF-13) ─────────────────────────────────────────
    const ti = assistant?.typed_input || {};
    const channelMode = assistant?.channels?.mode || 'restricted';
    const authorizedChannels = assistant?.channels?.authorized_channels || [];
    const {
        items: channelItems
    } = useResourcePickerData('channel');
    const channelLabelMap = useMemo(
        () => Object.fromEntries(channelItems.map((c) => [c.id, c.label])),
        [channelItems]
    );
    const allOpcodes = (assistant?.opcodes || [])
        .map((e) => ({
            id: e.opcode_id,
            purpose: e.purpose
        }))
        .filter((e) => e.id);

    const mappingRefs = useMemo(() => {
        const refs = {
            context: new Set(),
            propensity: new Set(),
            entity: new Set()
        };
        for (const entry of (assistant?.opcodes || [])) {
            for (const target of Object.values(entry.input_mapping || {})) {
                const m = (target || '').match(/^(context|propensity|entity)\.(.+)$/);
                if (m) refs[m[1]].add(m[2]);
            }
        }
        return refs;
    }, [assistant]);

    const declaredContextKeys = useMemo(() => ti.context_keys || [], [ti]);
    const declaredPropensityKeys = useMemo(() => ti.propensity_keys || [], [ti]);
    const entityProps = ti.entity_schema?.properties || {};
    const declaredEntityKeys = useMemo(() => Object.keys(entityProps), [entityProps]);
    const requiredEntityKeys = useMemo(
        () => new Set(Array.isArray(ti.entity_schema?.required) ? ti.entity_schema.required : []),
        [ti.entity_schema]
    );

    const neededContextKeys = useMemo(() => Array.from(new Set(
        declaredContextKeys.concat(Array.from(mappingRefs.context))
    )), [declaredContextKeys, mappingRefs.context]);
    const neededPropensityKeys = useMemo(() => Array.from(new Set(
        declaredPropensityKeys.concat(Array.from(mappingRefs.propensity))
    )), [declaredPropensityKeys, mappingRefs.propensity]);
    const neededEntityKeys = useMemo(() => Array.from(new Set(
        declaredEntityKeys.concat(Array.from(mappingRefs.entity))
    )), [declaredEntityKeys, mappingRefs.entity]);

    const isImplicit = (cat, k) => mappingRefs[cat].has(k) && (
        cat === 'context' ? !declaredContextKeys.includes(k)
            : cat === 'propensity' ? !declaredPropensityKeys.includes(k)
                : !declaredEntityKeys.includes(k)
    );

    // ── Form state ──────────────────────────────────────────────────────
    const [contextVals, setContextVals] = useState({});
    const [propensityVals, setPropensityVals] = useState({});
    const [entityVals, setEntityVals] = useState({});
    const [intent, setIntent] = useState('');
    const [opcodeOverride, setOpcodeOverride] = useState('');
    const [channelId, setChannelId] = useState(channelMode === 'restricted' ? authorizedChannels[0] || '' : '');
    const memoryLogId = useMemo(() => `dock-${Date.now()}`, [assistant?.assistant_id]);

    useEffect(() => { setContextVals((p) => mapInit(neededContextKeys, p)); }, [neededContextKeys]);
    useEffect(() => { setPropensityVals((p) => mapInit(neededPropensityKeys, p)); }, [neededPropensityKeys]);
    useEffect(() => { setEntityVals((p) => mapInit(neededEntityKeys, p)); }, [neededEntityKeys]);

    const totalSet = countSet(contextVals) + countSet(propensityVals) + countSet(entityVals)
        + (intent.trim() ? 1 : 0);
    const totalNeeded = neededContextKeys.length + neededPropensityKeys.length + neededEntityKeys.length + 1;

    // ── Run state ───────────────────────────────────────────────────────
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null); // { ok, raw, error, ts }
    const [setupError, setSetupError] = useState(null);

    function clearAll() {
        setContextVals(mapInit(neededContextKeys, {}));
        setPropensityVals(mapInit(neededPropensityKeys, {}));
        setEntityVals(mapInit(neededEntityKeys, {}));
        setIntent('');
        setOpcodeOverride('');
    }

    function buildTypedInput() {
        return {
            context: collect(contextVals),
            propensity: collect(propensityVals),
            intent: intent.trim() || null,
            entity: collect(entityVals, (k) => entityProps[k]?.type)
        };
    }

    async function run() {
        if (running) return;
        if (!assistant?.assistant_id) {
            setSetupError('No assistant id. Save the assistant first.');
            return;
        }
        // `has_token` is what a loaded doc carries — the token itself is never
        // returned to the browser. A doc the user just edited locally may hold a
        // freshly typed/regenerated token that isn't saved yet; either way the
        // DE is runnable only if the SERVER has one, so fall back to the local
        // field only when `has_token` is absent (pre-redaction server).
        const deHasToken = assistant?.has_token ?? !!assistant?.assistant_token;
        if (!deHasToken) {
            setSetupError(
                'This assistant has no assistant_token configured. A2A requires a Bearer token — '
                + 'set or regenerate the token in Identity & Voice, then Save.'
            );
            return;
        }
        setSetupError(null);
        setRunning(true);
        setResult(null);

        // No token here: the run goes through bex-server, which resolves the
        // DE's credential itself.
        const cfg = {
            orgId: oragID || assistant.org_id || '',
            subOrgId: subOragID || assistant.sub_org_id || '',
            assistantId: assistant.assistant_id
        };
        const opts = {
            typedInput: buildTypedInput(),
            memoryLogId,
            channelId: channelId || undefined,
            opcodeId: opcodeOverride || undefined
        };

        try {
            const res = await a2aClient.sendMessage(cfg, opts);
            setResult({
                raw: res,
                ts: Date.now()
            });
        } catch (err) {
            const raw = err?.response?.data || err?.message || String(err);
            // Extract the human-readable message from nested error objects
            // e.g. { ok: false, error: { message: '...' } } or { message: '...' }
            const errorMsg = (raw && typeof raw === 'object')
                ? (raw?.error?.message || raw?.message || safeStringify(raw))
                : raw;
            setResult({
                error: errorMsg,
                ts: Date.now()
            });
        } finally {
            setRunning(false);
        }
    }

    return (
        <div className={cn(
            'flex h-full overflow-hidden bg-card/30 relative',
            maximized ? 'flex-col lg:flex-row' : 'flex-col'
        )}>
            {/* ── UNSAVED BLOCKER ───────────────────────────────────── */}
            {isDirty && <UnsavedBlocker onSaveFirst={onSaveFirst} />}
            {/* ── INPUTS PANE ───────────────────────────────────────── */}
            <div className={cn(
                'flex flex-col min-h-0',
                maximized ? 'lg:w-[440px] lg:flex-shrink-0 lg:border-r' : 'border-b max-h-[55%]'
            )}>
                <FormHeader
                    fieldsSet={totalSet}
                    fieldsNeeded={totalNeeded}
                    onClear={totalSet > 0 ? clearAll : null}
                />
                <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-4">
                    {setupError && (
                        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1.5 text-[12px] text-destructive">
                            <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                            <span>{setupError}</span>
                        </div>
                    )}

                    <Section title="Intent" hint="what the user is saying">
                        <Textarea
                            value={intent}
                            onChange={(e) => setIntent(e.target.value)}
                            placeholder="e.g. Look up order ORD-123456"
                            rows={maximized ? 4 : 3}
                            className="resize-y text-[13px]"
                        />
                    </Section>

                    <Section title="Context" hint={`${neededContextKeys.length} key${neededContextKeys.length === 1 ? '' : 's'}`}>
                        {neededContextKeys.length > 0 ? neededContextKeys.map((k) => (
                            <TypedField
                                key={k}
                                name={k}
                                type="string"
                                value={contextVals[k] ?? ''}
                                onChange={(v) => setContextVals((p) => ({
                                    ...p,
                                    [k]: v
                                }))}
                                badge={isImplicit('context', k) ? 'implicit' : null}
                            />
                        )) : <Empty>No declared context keys.</Empty>}
                    </Section>

                    <Section title="Propensity" hint={`${neededPropensityKeys.length} key${neededPropensityKeys.length === 1 ? '' : 's'}`}>
                        {neededPropensityKeys.length > 0 ? neededPropensityKeys.map((k) => (
                            <TypedField
                                key={k}
                                name={k}
                                type="string"
                                value={propensityVals[k] ?? ''}
                                onChange={(v) => setPropensityVals((p) => ({
                                    ...p,
                                    [k]: v
                                }))}
                                badge={isImplicit('propensity', k) ? 'implicit' : null}
                            />
                        )) : <Empty>No declared propensity keys.</Empty>}
                    </Section>

                    <Section title="Entity" hint={`${neededEntityKeys.length} field${neededEntityKeys.length === 1 ? '' : 's'}`}>
                        {neededEntityKeys.length > 0 ? neededEntityKeys.map((k) => {
                            const prop = entityProps[k] || {};
                            return (
                                <TypedField
                                    key={k}
                                    name={k}
                                    type={prop.type || 'string'}
                                    required={requiredEntityKeys.has(k)}
                                    description={prop.description}
                                    constraint={extractConstraint(prop)}
                                    value={entityVals[k] ?? ''}
                                    onChange={(v) => setEntityVals((p) => ({
                                        ...p,
                                        [k]: v
                                    }))}
                                    badge={isImplicit('entity', k) ? 'implicit' : null}
                                />
                            );
                        }) : <Empty>No declared entity_schema properties.</Empty>}
                    </Section>

                    <Section title="Routing &amp; Memory" hint="advanced">
                        <div className="space-y-2.5">
                            <div className="space-y-1">
                                <Label className="flex items-center gap-1.5 text-[11.5px] font-medium text-foreground">
                                    OpCode override
                                    <span className="text-[10px] font-normal text-muted-foreground">optional · skips intent routing</span>
                                </Label>
                                <Select value={opcodeOverride || '__none__'} onValueChange={(v) => setOpcodeOverride(v === '__none__' ? '' : v)}>
                                    <SelectTrigger className="h-8 text-[12.5px]"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">
                                            <span className="text-[12.5px] text-muted-foreground italic">
                                                auto · route via intent ({assistant?.default_opcode || 'no default'})
                                            </span>
                                        </SelectItem>
                                        {allOpcodes.map((op) => (
                                            <SelectItem key={op.id} value={op.id}>
                                                <span className="font-mono text-[11.5px]">
                                                    {op.id}{op.purpose ? ` — ${op.purpose}` : ''}
                                                </span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1">
                                <Label className="flex items-center gap-1.5 text-[11.5px] font-medium text-foreground">
                                    Channel
                                    <span className="text-[10px] font-normal text-muted-foreground">{channelMode}</span>
                                </Label>
                                {channelMode === 'restricted' && authorizedChannels.filter(Boolean).length > 0 ? (
                                    <Select value={channelId} onValueChange={setChannelId}>
                                        <SelectTrigger className="h-8 text-[12.5px]">
                                            <SelectValue placeholder="Pick authorized channel" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {authorizedChannels.filter(Boolean).map((id) => (
                                                <SelectItem key={id} value={id}>
                                                    <span>{channelLabelMap[id] || id}</span>
                                                    {channelLabelMap[id] && (
                                                        <span className="ml-1.5 font-mono text-[10px] text-muted-foreground">{id}</span>
                                                    )}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                ) : (
                                    <Input
                                        value={channelId}
                                        onChange={(e) => setChannelId(e.target.value)}
                                        placeholder={channelMode === 'open' ? 'any channel_id (or blank)' : '(restricted, no channels — will 403)'}
                                        className="h-8 text-[12.5px]"
                                    />
                                )}
                            </div>
                            <div className="text-[10.5px] text-muted-foreground">
                                memory_log_id&nbsp;
                                <span className="font-mono">{memoryLogId}</span>
                            </div>
                        </div>
                    </Section>
                </div>

                <div className="flex-shrink-0 border-t bg-background px-3 py-2 flex items-center justify-end gap-2">
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={clearAll}
                        disabled={running || totalSet === 0}
                    >
                        <RotateCcw className="mr-1 h-3 w-3" /> Clear
                    </Button>
                    <Button
                        size="sm"
                        onClick={run}
                        disabled={running || !assistant?.assistant_id}
                    >
                        {running ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Play className="mr-1 h-3 w-3" />}
                        {running ? 'Running' : 'Run'}
                    </Button>
                </div>
            </div>

            {/* ── RESULT PANE ───────────────────────────────────────── */}
            <div className="flex-1 min-h-0 overflow-y-auto p-3 bg-muted/10">
                <ResultBlock
                    running={running}
                    result={result}
                    assistantTitle={assistant?.title}
                />
            </div>
        </div>
    );
}

// ─── Sub-components ───────────────────────────────────────────────────

function FormHeader({
    fieldsSet, fieldsNeeded, onClear
}) {
    return (
        <div className="flex-shrink-0 flex items-center justify-between border-b bg-card px-3 py-2">
            <div className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-foreground">
                <Compass className="h-3.5 w-3.5 text-muted-foreground" />
                Inputs
                <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 font-mono text-[10px] font-normal text-muted-foreground">
                    {fieldsSet}/{fieldsNeeded}
                </span>
            </div>
            {onClear && (
                <Button variant="ghost" size="sm" className="h-7 px-2 text-[11.5px]" onClick={onClear}>
                    <RotateCcw className="mr-1 h-3 w-3" /> Clear
                </Button>
            )}
        </div>
    );
}

function Section({
    title, hint, children
}) {
    return (
        <div className="space-y-1.5">
            <div className="flex items-center justify-between border-b border-dashed pb-0.5">
                <h4 className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{title}</h4>
                {hint && <span className="text-[10px] text-muted-foreground">{hint}</span>}
            </div>
            <div className="space-y-2.5">{children}</div>
        </div>
    );
}

function Empty({
    children
}) {
    return (
        <div className="rounded-md border border-dashed bg-muted/20 px-2.5 py-1.5 text-[11px] text-muted-foreground">
            {children}
        </div>
    );
}

function ResultBlock({
    running, result, assistantTitle
}) {
    if (!running && !result) {
        return (
            <div className="text-sm text-muted-foreground">
                Fill the inputs and click <b>Run</b>. The result, routing decision, and full
                A2A envelope will appear here.
            </div>
        );
    }
    if (running) {
        return (
            <div className="flex items-center gap-2 rounded-md border bg-card px-3 py-2.5 text-sm italic text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> running…
            </div>
        );
    }

    const {
        raw, error
    } = result;
    if (error) {
        return (
            <FailedBlock
                title="Request failed"
                detail={typeof error === 'string' ? error : safeStringify(error)}
            />
        );
    }

    const isFailed = (Array.isArray(raw?.errors) && raw.errors.length > 0)
        || (typeof raw?.state === 'string' && raw.state.includes('FAILED'));
    const routing = (raw?.output && typeof raw.output === 'object') ? raw.output._bex_routing : null;
    const display = renderableText(raw);

    return (
        <div className="space-y-3">
            {/* Status */}
            <div className={cn(
                'rounded-md border px-3 py-2 flex items-start gap-2',
                isFailed
                    ? 'border-destructive/40 bg-destructive/5'
                    : 'border-emerald-300/50 bg-emerald-50/40'
            )}>
                {isFailed
                    ? <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" />
                    : <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-700" />
                }
                <div className="min-w-0 flex-1">
                    <div className={cn(
                        'text-[12.5px] font-semibold',
                        isFailed ? 'text-destructive' : 'text-emerald-800'
                    )}>
                        {isFailed ? 'Failed' : 'Completed'}
                    </div>
                    <div className="text-[10.5px] text-muted-foreground font-mono">
                        {raw?.state || ''}{raw?.taskId ? ` · ${raw.taskId}` : ''}
                    </div>
                </div>
            </div>

            {/* Routing decision */}
            {routing && (
                <div className="rounded-md border border-dashed bg-muted/30 px-3 py-2">
                    <div className="flex items-center gap-1.5 mb-0.5">
                        <Compass className="h-3 w-3 text-muted-foreground" />
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">routing</span>
                        {routing.resolution_mode && (
                            <span className="ml-auto rounded-full bg-card px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground border">
                                {routing.resolution_mode}
                            </span>
                        )}
                    </div>
                    <div className="font-mono text-[12px]">
                        → {routing.selected_opcode_id || '—'}
                    </div>
                    {routing.reason && (
                        <div className="mt-0.5 text-[10.5px] text-muted-foreground">
                            {routing.reason}
                        </div>
                    )}
                </div>
            )}

            {/* Output / errors */}
            <div className="rounded-md border bg-card px-3 py-2.5">
                <div className="mb-1 flex items-center gap-1.5">
                    <Bot className="h-3 w-3 text-muted-foreground" />
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {isFailed ? 'errors' : (assistantTitle || 'response')}
                    </span>
                </div>
                <pre className={cn(
                    'whitespace-pre-wrap break-words text-[12.5px] leading-snug',
                    isFailed ? 'text-destructive font-mono' : 'text-foreground'
                )}>
                    {display}
                </pre>
            </div>

            {/* Trace */}
            <details className="rounded-md border bg-card px-3 py-2 text-[11.5px]">
                <summary className="cursor-pointer flex items-center gap-1 text-muted-foreground hover:text-foreground">
                    <ChevronRight className="h-3 w-3" />
                    Full A2A envelope
                </summary>
                <pre className="mt-2 max-h-72 overflow-y-auto whitespace-pre-wrap break-words rounded border bg-muted/20 px-2 py-1.5 font-mono text-[10.5px]">
                    {safeStringify({
                        state: raw?.state,
                        taskId: raw?.taskId,
                        output: raw?.output,
                        errors: raw?.errors,
                        task: raw?.task
                    })}
                </pre>
            </details>
        </div>
    );
}

function FailedBlock({
    title, detail
}) {
    return (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2.5">
            <div className="flex items-center gap-1.5">
                <AlertCircle className="h-4 w-4 text-destructive" />
                <span className="text-[12.5px] font-semibold text-destructive">{title}</span>
            </div>
            <pre className="mt-1 whitespace-pre-wrap break-words font-mono text-[11.5px] text-destructive">
                {detail}
            </pre>
        </div>
    );
}

// ─── Helpers ───────────────────────────────────────────────────────

function mapInit(keys, prev) {
    const next = {};
    for (const k of keys) next[k] = prev[k] ?? '';
    return next;
}

function countSet(map) {
    return Object.values(map).filter((v) => v !== '' && v != null).length;
}

function collect(map, getType) {
    const out = {};
    for (const [k, v] of Object.entries(map)) {
        if (v === '' || v == null) continue;
        out[k] = coerceTypedFieldValue(v, getType ? (getType(k) || 'string') : 'string');
    }
    return out;
}

function extractConstraint(prop) {
    if (!prop || typeof prop !== 'object') return null;
    const c = {};
    if (prop.pattern) c.pattern = prop.pattern;
    if (prop.minLength != null) c.minLength = prop.minLength;
    if (prop.maxLength != null) c.maxLength = prop.maxLength;
    if (prop.minimum != null) c.minimum = prop.minimum;
    if (prop.maximum != null) c.maximum = prop.maximum;
    if (Array.isArray(prop.enum)) c.enum = prop.enum;
    return Object.keys(c).length ? c : null;
}

function renderableText(result) {
    if (!result) return '(no response)';
    if (Array.isArray(result.errors) && result.errors.length) return result.errors.join('\n');
    const out = result.output;
    if (out == null) return result.state ? `(${result.state})` : '(no output)';
    if (typeof out === 'string') return out;
    if (typeof out === 'object') {
        const candidates = [out.reply, out.text, out.message, out.response, out.answer, out.output];
        for (const c of candidates) {
            if (typeof c === 'string' && c.trim()) return c;
        }
        const {
            _bex_routing, ...rest
        } = out;
        return safeStringify(rest);
    }
    return String(out);
}

function safeStringify(v) {
    try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}
